/**
 * Restaurant model — shapes API restaurant payloads with defaults and derived values.
 * Use Restaurant.fromApi(apiResponse) when consuming restaurant list/detail/update endpoints.
 *
 * Backend (RestaurantController::restaurantPayload): uuid, name, slug, public_url,
 * address, latitude, longitude, phone, email, website, social_links, default_locale,
 * languages, logo_url, banner_url, created_at, updated_at.
 * operating_hours may be returned by some endpoints or set client-side.
 * timezone: IANA name for analytics periods and quick-order timestamps (nullable).
 */

const DEFAULT_SOCIAL_LINKS = {
  facebook: '',
  instagram: '',
  twitter: '',
  linkedin: '',
}

export default class Restaurant {
  constructor(data = {}) {
    this._uuid = data.uuid ?? null
    this._businessType = data.business_type ?? data.businessType ?? ''
    this._name = data.name ?? ''
    this._slug = data.slug ?? null
    this._publicUrl = data.public_url ?? data.publicUrl ?? ''
    this._customDomain = data.custom_domain ?? data.customDomain ?? null
    this._address = data.address ?? ''
    this._tagline = data.tagline ?? ''
    this._primaryColor = data.primary_color ?? data.primaryColor ?? null
    this._latitude = data.latitude ?? null
    this._longitude = data.longitude ?? null
    this._googleMapsUrl = data.google_maps_url ?? data.googleMapsUrl ?? null
    this._phone = data.phone ?? ''
    this._email = data.email ?? ''
    this._website = data.website ?? ''
    this._description = data.description ?? ''
    const rawSocial = data.social_links ?? data.socialLinks ?? {}
    this._socialLinks = {
      facebook: rawSocial.facebook ?? '',
      instagram: rawSocial.instagram ?? '',
      twitter: rawSocial.twitter ?? '',
      linkedin: rawSocial.linkedin ?? '',
    }
    this._defaultLocale = data.default_locale ?? data.defaultLocale ?? 'en'
    this._languages = Array.isArray(data.languages) ? [...data.languages] : []
    this._logoUrl = data.logo_url ?? data.logoUrl ?? null
    this._bannerUrl = data.banner_url ?? data.bannerUrl ?? null
    this._operatingHours = data.operating_hours !== undefined ? data.operating_hours : (data.operatingHours !== undefined ? data.operatingHours : {})
    this._createdAt = data.created_at ?? data.createdAt ?? null
    this._updatedAt = data.updated_at ?? data.updatedAt ?? null
    this._currency = data.currency ?? 'USD'
    this._template = data.template ?? 'template-1'
    this._yearEstablished = data.year_established ?? null
    this._reservationSmsTo = data.reservation_sms_to ?? data.reservationSmsTo ?? null
    this._pendingReservationsCount = data.pending_reservations_count ?? data.pendingReservationsCount ?? 0
    this._hideFooterCredits = data.hide_footer_credits === true
    this._showCurrency = data.show_currency !== false
    this._isQuickOrderEnabled = data.is_quick_order_enabled === true
    this._isQuickOrderViaAppEnabled = data.is_quick_order_via_app_enabled !== false
    this._timezone = data.timezone ?? null
  }

  get uuid() {
    return this._uuid
  }

  /** One of: restaurants, ecommerce, services */
  get business_type() {
    return this._businessType
  }

  get name() {
    return this._name
  }

  get slug() {
    return this._slug
  }

  get publicUrl() {
    return this._publicUrl
  }

  /** VIP only. When set, QR redirect and public URL use this domain; CORS allows it for API calls. */
  get custom_domain() {
    return this._customDomain
  }

  get address() {
    return this._address
  }

  get tagline() {
    return this._tagline
  }

  get primary_color() {
    return this._primaryColor
  }

  get latitude() {
    return this._latitude
  }

  get longitude() {
    return this._longitude
  }

  get google_maps_url() {
    return this._googleMapsUrl
  }

  get phone() {
    return this._phone
  }

  get email() {
    return this._email
  }

  get website() {
    return this._website
  }

  get description() {
    return this._description
  }

  get social_links() {
    return this._socialLinks
  }

  get default_locale() {
    return this._defaultLocale
  }

  get languages() {
    return this._languages
  }

  get logo_url() {
    return this._logoUrl
  }

  get banner_url() {
    return this._bannerUrl
  }

  get operating_hours() {
    return this._operatingHours
  }

  get created_at() {
    return this._createdAt
  }

  get updated_at() {
    return this._updatedAt
  }

  get currency() {
    return this._currency
  }

  get template() {
    return this._template
  }

  get year_established() {
    return this._yearEstablished
  }

  /** International mobile (+country code) for reservation SMS notifications (owner). */
  get reservation_sms_to() {
    return this._reservationSmsTo
  }

  /** Number of reservations with status pending (for badge). */
  get pending_reservations_count() {
    return this._pendingReservationsCount
  }

  /** When true, public site hides the "Site created via QRestoo" / credits block. Paid users only can set true. */
  get hide_footer_credits() {
    return this._hideFooterCredits
  }

  get is_quick_order_enabled() {
    return this._isQuickOrderEnabled
  }

  /** Pro/VIP: when true, guests may complete checkout via the mobile app flow (API); when false, messaging handoff only if contactable. */
  get is_quick_order_via_app_enabled() {
    return this._isQuickOrderViaAppEnabled
  }

  /** IANA timezone for analytics / order display; may be null (server uses UTC fallback). */
  get timezone() {
    return this._timezone
  }

  /**
   * Public site root URL for QR and links: VIP custom domain when set, else API `public_url`.
   * Accepts a restaurant-like plain object (e.g. from toJSON()). No trailing slash.
   */
  static publicSiteBaseUrl(restaurantLike = {}) {
    const cd = String(restaurantLike.custom_domain ?? restaurantLike.customDomain ?? '').trim()
    if (cd) {
      const host = cd.replace(/^https?:\/\//i, '').split('/')[0]
      return `https://${host}`.replace(/\/+$/, '')
    }
    const pu = String(restaurantLike.public_url ?? restaurantLike.publicUrl ?? '').trim()
    if (pu) return pu.replace(/\/+$/, '')
    return ''
  }

  /** Build from API response (data object or { data } wrapper). Use when consuming restaurant APIs. */
  static fromApi(apiResponse) {
    const data = apiResponse?.data ?? apiResponse
    return new Restaurant(data ?? {})
  }

  /**
   * Plain object for v-model / template binding and for sending payloads to API.
   * Components can use this when they need a reactive object; assign back to model for updates.
   */
  toJSON() {
    return {
      uuid: this._uuid,
      business_type: this._businessType,
      name: this._name,
      slug: this._slug,
      public_url: this._publicUrl,
      custom_domain: this._customDomain,
      address: this._address,
      tagline: this._tagline,
      primary_color: this._primaryColor,
      latitude: this._latitude,
      longitude: this._longitude,
      google_maps_url: this._googleMapsUrl,
      phone: this._phone,
      email: this._email,
      website: this._website,
      description: this._description,
      social_links: { ...this._socialLinks },
      default_locale: this._defaultLocale,
      languages: [...this._languages],
      logo_url: this._logoUrl,
      banner_url: this._bannerUrl,
      operating_hours: this._operatingHours == null ? null : (typeof this._operatingHours === 'object' ? { ...this._operatingHours } : {}),
      created_at: this._createdAt,
      updated_at: this._updatedAt,
      currency: this._currency,
      template: this._template,
      year_established: this._yearEstablished,
      reservation_sms_to: this._reservationSmsTo,
      pending_reservations_count: this._pendingReservationsCount,
      hide_footer_credits: this._hideFooterCredits,
      show_currency: this._showCurrency,
      is_quick_order_enabled: this._isQuickOrderEnabled,
      is_quick_order_via_app_enabled: this._isQuickOrderViaAppEnabled,
      timezone: this._timezone,
    }
  }
}
